import { useEffect, useRef, useState } from "react"

import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined"
import { Box, SvgIcon as MUISvgIcon, Stack, Typography } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"

import { gsap } from "@ggs/ui/local/gsap"
import type { FC, FCWC, ResponsiveImageType } from ".."
import { Button, Container, IconButton, Link, ResponsiveImage, SvgIcon } from ".."
import type { LinkType } from "../../graphql/types"
import useIsomorphicLayoutEffect from "../../helpers/useIsomorphicLayoutEffect"

export type StaffProfileCollectionItem = {
  image: ResponsiveImageType
  title: string
  subtitle: string
  uri: string
}

export type StaffProfileCollectionProps = {
  title: string
  subtitle?: string
  link?: LinkType
  items: StaffProfileCollectionItem[]
  expand?: boolean
}

const StaffProfileCollection: FC<StaffProfileCollectionProps> = ({
  title,
  subtitle,
  link,
  items,
}) => {
  const [profiles, setProfiles] = useState(items.slice(0, 12))
  const [expanded, setExpanded] = useState(false)

  const showExpandCta = items?.length > 12

  useEffect(() => {
    if (items) setProfiles(items.slice(0, 12))
  }, [items])

  useEffect(() => {
    if (expanded) setProfiles(items)
    else setProfiles(items.slice(0, 12))
  }, [expanded])

  return (
    <StaffProfileCollectionSection>
      <AnimatedSvgIcon />
      <Container maxWidth={1440} overflow="hidden">
        <StaffProfileCollectionHeader
          alignItems="center"
          textAlign="center"
          rowGap={{ xs: 5, md: 10 }}
          position="relative"
        >
          {subtitle && (
            <Typography
              variant="body.lg"
              component="h3"
              color={(theme) => theme.palette["neutral.700"].main}
              zIndex={1}
            >
              {subtitle}
            </Typography>
          )}
          <Typography
            variant="heading.3xl"
            component="h2"
            color={(theme) => theme.palette["neutral.700"].main}
            zIndex={1}
          >
            {title}
          </Typography>
        </StaffProfileCollectionHeader>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          }}
          gap={{ xs: 3, md: 10 }}
        >
          {profiles?.map((item, i) => (
            <StaffProfileCollectionItem key={i} {...item} />
          ))}
        </Box>
        <Box mt={{ xs: 15, md: 30 }} pb={1.5} textAlign="center">
          {link && (
            <Link href={link.uri} variant="link.md">
              {link.title}
            </Link>
          )}
          {showExpandCta && (
            <Button
              variant="secondary"
              onClick={() => setExpanded((state) => !state)}
              sx={{
                display: "flex",
                width: "100%",
              }}
            >
              {expanded ? "Less Profiles" : "View All Profiles"}
            </Button>
          )}
        </Box>
      </Container>
    </StaffProfileCollectionSection>
  )
}
export default StaffProfileCollection

/** A sub-component for the Staff profile collection Container */
const StaffProfileCollectionSection: FCWC = ({ children }) => {
  return (
    <Box
      pt={{ xs: 25, md: 55 }}
      pb={{ xs: 25, md: 36 }}
      position="relative"
      overflow="hidden"
      bgcolor={(theme) => theme.palette["tertiary.300"].main}
    >
      {children}
    </Box>
  )
}

/** A sub-component for the Staff profile collection Header */
const StaffProfileCollectionHeader = styled(Stack)(
  ({ theme }) => `
    margin: 0 auto;
    max-width: 878px;
    margin-bottom: ${theme.spacing(20)};
    width: 100%;

    ${theme.breakpoints.up("md")} {
      margin-bottom: ${theme.spacing(41)};
    }
  `
)

/** A sub-component for the Staff Card Item */
const StaffProfileCollectionItem: FC<StaffProfileCollectionItem> = ({
  title,
  subtitle,
  image,
  uri,
}) => {
  return (
    <Box
      position="relative"
      overflow="hidden"
      borderRadius={1.5}
      maxWidth={326}
      sx={(theme) => ({
        "isolation": "isolate",
        "transition": ".3s",
        "&:hover": {
          boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.10)",
          img: { transform: "scale(1.05)" },
          button: { opacity: 1, bottom: 17 },
          [theme.breakpoints.down("md")]: {
            button: { display: "none" },
          },
        },
      })}
    >
      <Link type="overlay" href={uri} aria-label={title} title={title} />
      <Box
        sx={{
          position: "relative",
        }}
      >
        {image ? (
          <ResponsiveImage
            image={image}
            sx={{
              display: "block",
              width: "100%",
              height: { xs: 210, md: 380 },
              transition: (theme) =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.standard * 1.5,
                }),
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            bgcolor={(theme) => theme.palette["tertiary.800"].main}
            borderRadius={1.5}
            sx={{ width: 326, height: 380 }}
          >
            <ImageNotSupportedOutlinedIcon />
          </Stack>
        )}
        <StaffProfileCollectionIcon />
      </Box>
      <StaffProfileCollectionItemLabel
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        gap={{ xs: 2, md: 1 }}
        color={(theme) => theme.palette["neutral.700"].main}
      >
        <Typography variant="body.lg" component="h2">{title}</Typography>
        <Typography variant="body.xs" component="h3" textTransform="uppercase" mb={0}>
          {subtitle}
        </Typography>
      </StaffProfileCollectionItemLabel>
    </Box>
  )
}

/** A sub-component for the Staff Card Icon */
const StaffProfileCollectionIcon: FC = () => {
  return (
    <Box
      sx={(theme) => ({
        "display": "flex",
        "borderRadius": "100px",
        "background": theme.palette["primary.800"].main,
        "&:hover": { cursor: "pointer" },
        [theme.breakpoints.down("md")]: {
          display: "none",
        },
      })}
    >
      <IconButton
        variant="quaternary"
        aria-label="View Profile"
        icon={
          <SvgIcon
            variant="arrowOutward"
            size={18}
            sx={{
              fill: (theme) => theme.palette["neutral.100"].main,
            }}
          />
        }
        sx={{
          transition: ".3s",
          opacity: 0,
          position: "absolute",
          bottom: 0,
          right: 17,
        }}
      />
    </Box>
  )
}

/** A sub-component for the Staff Card Label in the Grid Item */
const StaffProfileCollectionItemLabel = styled(Stack)(
  ({ theme }) => `
    background-color: ${theme.palette["neutral.100"].main};
    padding: ${theme.spacing(4, 3)};
    position: relative;

    ${theme.breakpoints.up("md")} {
      padding: ${theme.spacing(5, 5, 6)};
      position: relative;
    }
  `
)

/** A sub-component for the Animated SVG that appears in the bg */
const AnimatedSvgIcon: FC = () => {
  const theme = useTheme()
  const mainRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const pointRef = useRef<SVGGElement>(null)

  useIsomorphicLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(mainRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 75%",
          end: "+=10px",
          scrub: true,
          // markers: true,
        },
      })
      gsap.to(pathRef.current, {
        strokeDashoffset: 849,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 50%",
          end: "top 15%",
          scrub: true,
          // markers: true,
        },
      })
      gsap.to(pointRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 15%",
          end: "top 5%",
          scrub: true,
        },
      })
    }, mainRef) // <- Scope!
    return () => ctx.revert() // <- Cleanup!
  }, [mainRef, pathRef, pointRef])

  return (
    <Box
      ref={mainRef}
      sx={{
        width: 789,
        height: 337,
        position: "absolute",
        top: 165,
        display: { xs: "none", md: "block" },
        opacity: 0,

        [theme.breakpoints.down(1600)]: {
          left: 1000,
        },

        [theme.breakpoints.between(1600, 1803)]: {
          right: -203,
        },

        [theme.breakpoints.up(1803)]: {
          right: 0,
        },
      }}
    >
      <MUISvgIcon
        viewBox="0 0 787 331"
        sx={{
          width: 787,
          height: 331,
        }}
      >
        <path
          d="M785.428 1.301C756.23 37.4045 731.395 80.1763 693.122 106.756C640.695 143.166 585.853 141.377 532.484 135.109C494.542 130.653 455.604 129.089 417.179 126.451C367.602 123.048 318.162 119.825 266.295 135.462C214.189 151.172 165.575 184.569 120.382 219.264C94.4555 239.168 66.3735 265.321 45.5039 291.126"
          fill="none"
          stroke={theme.palette["primary.800"].main}
          strokeWidth="2"
          strokeDasharray="0.1 10"
          strokeLinecap="round"
        />
        <path
          ref={pathRef}
          d="M785.428 1.301C756.23 37.4045 731.395 80.1763 693.122 106.756C640.695 143.166 585.853 141.377 532.484 135.109C494.542 130.653 455.604 129.089 417.179 126.451C367.602 123.048 318.162 119.825 266.295 135.462C214.189 151.172 165.575 184.569 120.382 219.264C94.4555 239.168 66.3735 265.321 45.5039 291.126"
          fill="none"
          stroke={theme.palette["tertiary.300"].main}
          strokeWidth="3"
          strokeDasharray={849}
          strokeDashoffset={849 * 2}
        />
        <g ref={pointRef} opacity="0">
          <path
            d="M46.8784 314.001C42.4652 326.5 28.7555 333.055 16.2569 328.641C3.75837 324.228 -2.79611 310.518 1.61709 298.02C6.03029 285.521 19.74 278.967 32.2386 283.38C44.7371 287.793 51.2916 301.503 46.8784 314.001Z"
            fill={theme.palette["primary.400"].main}
            fillOpacity="0.15"
          />
          <path
            d="M28.8197 307.825C28.0841 309.908 25.7992 311 23.7161 310.265C21.633 309.529 20.5406 307.244 21.2761 305.161C22.0116 303.078 24.2966 301.985 26.3797 302.721C28.4628 303.457 29.5552 305.742 28.8197 307.825Z"
            fill={theme.palette["primary.800"].main}
          />
        </g>
      </MUISvgIcon>
    </Box>
  )
}
